// Safety-critical component. The dismissal path is intentionally
// two-step. There is no X / close button, no Escape, no swipe-down,
// no overlay-click dismissal.
//
// Triggered by: daily check-in suicidal-ideation = YES, the "I'm in crisis"
// link in screen footers, or long-press on "Talk me down".
//
// Migrated to Radix Dialog so focus is correctly trapped inside the
// modal — pre-migration the tab focus could escape into the body content
// underneath. Radix handles focus trap, body scroll lock, and aria
// labelling automatically.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Phone, MessageSquare, MapPin, Building2 } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { CRISIS_RESOURCES } from '@shared/constants';

interface CrisisModalProps {
  open: boolean;
  onDismiss: () => void;
}

export function CrisisModal({ open, onDismiss }: CrisisModalProps) {
  const [confirming, setConfirming] = useState(false);

  // Safety: every fresh open must land on the crisis resources, never
  // mid-confirm. Reset the two-step dismissal state when the modal closes.
  useEffect(() => {
    if (!open) setConfirming(false);
  }, [open]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          // Block reflexive close paths — only the explicit confirm flow
          // can dismiss. If something tries to set open=false (Radix
          // internals do this on Esc/overlay), we ignore it and force
          // the state back via the consumer's onDismiss only after the
          // confirm step.
          return;
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="crisis-overlay" />
        <Dialog.Content
          className="crisis-content"
          aria-labelledby="crisis-title"
          aria-describedby="crisis-body"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <Dialog.Title id="crisis-title" className="crisis-title">
            You're not alone right now.
          </Dialog.Title>
          <Dialog.Description id="crisis-body" className="crisis-body">
            Withdrawal can bring intense thoughts. These feelings can pass, and people are
            trained for exactly this moment. Please reach out — you don't have to do this alone.
          </Dialog.Description>

          <a className="crisis-action" href={`tel:${CRISIS_RESOURCES.lifeline.tel}`}>
            <span className="crisis-action-icon">
              <Icon icon={Phone} />
            </span>
            <span className="crisis-action-body">
              <span className="crisis-action-label">Call 988</span>
              <span className="crisis-action-sub">
                Suicide and Crisis Lifeline — 24/7, free, confidential
              </span>
            </span>
          </a>

          <a
            className="crisis-action"
            href={`sms:${CRISIS_RESOURCES.text.sms}?&body=${CRISIS_RESOURCES.text.keyword}`}
          >
            <span className="crisis-action-icon">
              <Icon icon={MessageSquare} />
            </span>
            <span className="crisis-action-body">
              <span className="crisis-action-label">Text HOME to 741741</span>
              <span className="crisis-action-sub">Crisis Text Line — 24/7</span>
            </span>
          </a>

          <a className="crisis-action" href={`tel:${CRISIS_RESOURCES.samhsa.tel}`}>
            <span className="crisis-action-icon">
              <Icon icon={MapPin} />
            </span>
            <span className="crisis-action-body">
              <span className="crisis-action-label">SAMHSA Helpline · 1-800-662-4357</span>
              <span className="crisis-action-sub">Treatment referrals, free, confidential</span>
            </span>
          </a>

          <a className="crisis-action" href="tel:911">
            <span className="crisis-action-icon">
              <Icon icon={Building2} />
            </span>
            <span className="crisis-action-body">
              <span className="crisis-action-label">Go to the emergency room</span>
              <span className="crisis-action-sub">If you are in immediate danger, call 911</span>
            </span>
          </a>

          <div className="crisis-dismiss-row">
            {!confirming ? (
              <Button
                variant="secondary"
                size="lg"
                block
                onClick={() => setConfirming(true)}
                className="crisis-dismiss-btn"
              >
                I've seen these resources
              </Button>
            ) : (
              <div className="stack" style={{ '--stack-gap': 'var(--space-3)' } as React.CSSProperties}>
                <p className="crisis-confirm-prompt">Are you safe enough to continue?</p>
                <Button variant="accent" size="lg" block onClick={onDismiss}>
                  Yes, I'm okay for now
                </Button>
                <Button
                  variant="tertiary"
                  size="lg"
                  block
                  onClick={() => setConfirming(false)}
                  className="crisis-dismiss-btn-tertiary"
                >
                  No, please show me again
                </Button>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
